/**
 * Asymmetric and full-bleed layouts.
 *
 * Two things these need that a fitted band does not:
 *
 * - **asymmetric insets**, for "text on the left, device on the right" — which is where a wide
 *   iPad canvas has room the iPhone one does not;
 * - **a deliberate scale beyond the canvas**, for a device that runs off the bottom edge. That is
 *   a real and common design, and it needs nothing special from the collision check: the caption
 *   is painted *after* the device, so it sits on top of it rather than under it.
 */
import { Template } from '@keymaker/schema';
import { device, fittedDevice, gradient, solid, text } from '../kit';

export const offsetDeviceRight = Template.parse({
  id: 'offset-device-right',
  name: 'Offset device, right',
  category: 'offset',
  description:
    'A caption block on the left, with the device set lower and offset to the right so it reads larger than a true half-width column could.',
  tags: ['feature', 'asymmetric', 'pair'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#101A3D', '#3A1D8F'], 135),
    layers: [
      text({
        content: '{{caption.1}}',
        box: { x: 0.055, y: 0.06, width: 0.86, height: 0.22 },
        size: 0.040,
      }),
      // A half-width column cannot hold a portrait phone at a readable size: the region is 0.72
      // of the canvas and the device's own 2.04 aspect ratio then caps its height at 47%. Widening
      // the region and letting the device sit lower is what makes it large and still clear of the
      // caption.
      fittedDevice({ top: 0.33, bottom: 1, left: 0.28, right: 0 }),
    ],
  },
});

export const offsetDeviceLeft = Template.parse({
  id: 'offset-device-left',
  name: 'Offset device, left',
  category: 'offset',
  description: 'The mirror of the above, so a two-panel set can alternate and keep a rhythm.',
  tags: ['feature', 'asymmetric', 'pair'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#3A1D8F', '#101A3D'], 135),
    layers: [
      text({
        content: '{{caption.1}}',
        box: { x: 0.085, y: 0.06, width: 0.86, height: 0.22 },
        size: 0.040,
        align: 'right',
      }),
      fittedDevice({ top: 0.33, bottom: 1, left: 0, right: 0.28 }),
    ],
  },
});

export const bleedDeviceWithCaption = Template.parse({
  id: 'bleed-device-with-caption',
  name: 'Full-bleed device with caption',
  category: 'full-bleed',
  description:
    'The device is wider than the canvas and runs off the bottom; the caption sits over its top edge.',
  tags: ['hero', 'bold', 'bleed'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#1B7A5A', '#0B3B2C']),
    layers: [
      device({
        id: 'device',
        scale: 1.18,
        y: 1.0,
        anchor: 'bottom',
        shadow: { blur: 0.04, opacity: 0.45, offsetY: 0.008, color: '#04120D' },
      }),
      text({
        id: 'caption',
        content: '{{caption.1}}',
        box: { x: 0.06, y: 0.04, width: 0.88, height: 0.12 },
        size: 0.042,
        align: 'center',
      }),
    ],
  },
});

export const bleedDevicePlain = Template.parse({
  id: 'bleed-device-plain',
  name: 'Full-bleed device, no caption',
  category: 'full-bleed',
  description: 'Nothing but the device, cropped by the canvas. A clean closing panel.',
  tags: ['hero', 'minimal', 'bleed'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: solid('#0B1020'),
    layers: [
      device({
        id: 'device',
        scale: 1.15,
        y: 1.0,
        anchor: 'bottom',
      }),
    ],
  },
});

export const sideAndBleedTemplates = [
  offsetDeviceRight,
  offsetDeviceLeft,
  bleedDeviceWithCaption,
  bleedDevicePlain,
];
